import { api } from '../client';

export type MovementType =
  'PERMANENT' | 'TEMPORARY_IMPORT' | 'TEMPORARY_EXPORT' | 'LICENCE_REQUIRED';

export type PermitCaseType = 'TEMPORARY_IMPORT' | 'TEMPORARY_EXPORT' | 'LICENCE';

export type PermitCaseStatus =
  'DRAFT' | 'SUBMITTED' | 'IN_PROGRESS' | 'ISSUED' | 'REJECTED' | 'CLOSED' | 'CANCELLED';

export interface PermitCase {
  id: string;
  caseNumber: string;
  caseType: PermitCaseType;
  status: PermitCaseStatus;
  commercialInvoiceRef: string;
  issuingAuthority: string | null;
  permitReference: string | null;
  permitReceivedDate: string | null;
  permitExpiryDate: string | null;
  createdAt: string;
  countryRegime?: { countryCode: string; countryName: string };
  _count?: { lineItems: number };
}

export interface HeldRequest {
  id: string;
  movementType: MovementType;
  reason: string | null;
  isHeld: boolean;
  heldAt: string | null;
  transportOrder: {
    id: string;
    orderNumber: string;
    originCountry: string;
    destCountry: string;
    pickupDate: string;
  };
  permitCases: {
    id: string;
    caseNumber: string;
    status: PermitCaseStatus;
    permitReference: string | null;
    permitExpiryDate: string | null;
  }[];
}

export interface PermitLineItem {
  id: string;
  lineNumber: number;
  description: string;
  assetReference: string | null;
  serialNumber: string | null;
  hsCode: string | null;
  quantity: number;
  totalValue: number | null;
  currency: string | null;
  /** Null means the line did not match the asset master — flagged, not blocked. */
  equipmentId: string | null;
  equipment?: { id: string; assetNumber: string; name: string } | null;
}

export interface PermitCaseDetail extends PermitCase {
  lineItems: PermitLineItem[];
  complianceAssessment: {
    id: string;
    movementType: MovementType;
    isHeld: boolean;
    transportOrder: { orderNumber: string; originCountry: string; destCountry: string };
  };
  approvalStages: {
    id: string;
    sequence: number;
    authorityName: string;
    submittedAt: string | null;
    receivedAt: string | null;
  }[];
  securityInstrument: {
    id: string;
    instrumentType: string;
    amount: number;
    currency: string;
    status: string;
    recoveredAt: string | null;
  } | null;
}

export interface WatchlistRow {
  id: string;
  caseId: string;
  caseNumber: string;
  permitReference: string | null;
  country: string | null;
  description: string;
  assetReference: string | null;
  deadline: string;
  daysRemaining: number;
  /** Total days in country across the whole renewal chain. */
  cumulativeDays: number;
  status: string;
}

/** Banded by days remaining: the question is "what needs me now". */
export interface Watchlist {
  breached: WatchlistRow[];
  actNow: WatchlistRow[];
  planNow: WatchlistRow[];
  monitor: WatchlistRow[];
}

export interface RenewalLineage {
  chain: { id: string; caseNumber: string; permitExpiryDate: string | null }[];
  renewalCount: number;
  /** Total days in country across the whole chain, against the regime cap. */
  cumulativeDays: number;
}

export type DischargeEvidence = 'PROOF_OF_EXIT' | 'CUSTOMS_DISCHARGE_CERTIFICATE';

export interface CountryRegime {
  id: string;
  countryCode: string;
  countryName: string;
  currencies: string[];
  regimeValues: string[];
  organisationLevels: string[];
  authorityChain: string[];
  showCustomsDeclaration: boolean;
  showExemptionFlag: boolean;
  renewalModel: 'LINKED_CASE' | 'PROROGATION_STACK';
  /** This company's overrides, or null until it adopts the country. */
  config: {
    id: string;
    alertLeadDays: number;
    approvalBodies: string[];
    cumulativeAdmissionCap: number | null;
    requireSecurityRecovery: boolean;
    acceptedDischargeEvidence: DischargeEvidence[];
  } | null;
  isConfigured: boolean;
}

export interface LeadTimeReport {
  countries: {
    countryCode: string;
    countryName: string;
    authorities: {
      authorityName: string;
      medianDays: number;
      samples: number;
      slowestDays: number;
    }[];
    chainMedianDays: number;
    slowestStage: string | null;
    configuredLeadDays: number | null;
    /** A lead time at or below the measured chain leaves no room to renew. */
    leadTimeTooShort: boolean;
  }[];
  totalStagesMeasured: number;
}

export const permitsApi = {
  /** Requests held pending a permit — the queue the module exists to surface. */
  async getHeldRequests() {
    const res = await api.get<HeldRequest[]>('/permits/held-requests');
    return res.data;
  },

  async getCases(params?: {
    status?: string;
    caseType?: string;
    /** Narrow to the permit cases raised for one job. */
    jobId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    const res = await api.get<{
      data: PermitCase[];
      total: number;
      page: number;
      totalPages: number;
    }>('/permits/cases', { params });
    return res.data;
  },

  async getCase(id: string) {
    const res = await api.get<PermitCaseDetail>(`/permits/cases/${id}`);
    return res.data;
  },

  /** Recording all three fields is what makes the hold releasable. */
  async recordPermitIssued(
    id: string,
    body: { permitReference: string; permitReceivedDate: string; permitExpiryDate: string }
  ) {
    const res = await api.patch<PermitCase>(`/permits/cases/${id}/permit-issued`, body);
    return res.data;
  },

  async releaseHold(assessmentId: string) {
    const res = await api.post(`/permits/assessments/${assessmentId}/release-hold`, {});
    return res.data;
  },

  async getWatchlist() {
    const res = await api.get<Watchlist>('/permits/watchlist');
    return res.data;
  },

  /** Requires proof of exit; the server refuses without it. */
  async dischargeObligation(
    id: string,
    proofOfExitRef: string,
    evidenceType: DischargeEvidence = 'PROOF_OF_EXIT'
  ) {
    const res = await api.post(`/permits/obligations/${id}/discharge`, {
      proofOfExitRef,
      evidenceType,
    });
    return res.data;
  },

  /**
   * Extends a temporary admission. The server picks the shape from the
   * country's regime — a new linked case, or a prorogation on this one.
   */
  async renew(
    caseId: string,
    body: {
      newExpiryDate: string;
      reference?: string;
      rate?: number;
      dutyAndTax?: number;
      currency?: string;
    }
  ) {
    const res = await api.post<{
      model: 'LINKED_CASE' | 'PROROGATION_STACK';
      renewal?: { id: string };
    }>(`/permits/cases/${caseId}/renew`, body);
    return res.data;
  },

  async getLineage(caseId: string) {
    const res = await api.get<RenewalLineage>(`/permits/cases/${caseId}/lineage`);
    return res.data;
  },

  async getLeadTimeReport() {
    const res = await api.get<LeadTimeReport>('/permits/lead-time-report');
    return res.data;
  },

  async getCountries() {
    const res = await api.get<CountryRegime[]>('/permits/countries');
    return res.data;
  },

  /** A company's own settings for a country. Creating one adopts it. */
  async saveCountryConfig(body: {
    countryRegimeId: string;
    alertLeadDays?: number;
    approvalBodies?: string[];
    cumulativeAdmissionCap?: number | null;
    requireSecurityRecovery?: boolean;
    acceptedDischargeEvidence?: DischargeEvidence[];
  }) {
    const res = await api.post('/permits/countries/config', body);
    return res.data;
  },

  /** Whether an RFQ may be raised for a transport order. */
  async checkGate(transportOrderId: string) {
    const res = await api.get<{
      blocked: boolean;
      heldAssessment: boolean;
      openCaseNumbers: string[];
    }>(`/permits/gate/${transportOrderId}`);
    return res.data;
  },
};
